import { Principal } from "@icp-sdk/core/principal"

// The rows the Candid panels run in the browser: JS values for the generated
// client, and what each call must give back. These mirror the builder's
// challenges/candid_primitives and candid_strict_reply scripts, which are in
// Candid text and cannot run here; drift is accepted (ADR-0003). Values that
// need a malformed or wrongly typed message are command-line panels instead.

const alice = Principal.fromText("aaaaa-aa")
const allBytes = Uint8Array.from({ length: 256 }, (_, i) => i)
const vec40 = Array.from({ length: 40 }, (_, i) => BigInt(i % 2 ? -i : i))
const nested = { name: "n", tags: ["a", "b"], inner: { flag: true, n: [255] } }
const P = "candid_primitives_"
const S = "candid_strict_reply_"

// A row has one of: `expect`, a value compared by `same`; `fault`, the text after
// "fault: " the call must trap with; or `refusal`, the reason it must refuse with.
// `shown` is how the page describes the expectation.
const echo = (type, value, shown, expect = value) => ({
  label: `echo_${type} ${shown}`, method: `${P}echo_${type}`, args: [value], expect, shown,
})

const everything = {
  n: 1n, n8: 2, n16: 3, n32: 4, n64: 5n, i: -1n, i8: -2, i16: -3, i32: -4, i64: -5n, f32: 1.5, f64: 2.5,
  yes: true, t: "t", nul: null, res: null, p: alice, b: Uint8Array.of(0), o: [], v: [1n],
  r: { name: "n", tags: [], inner: { flag: false, n: [] } }, bare: { none: null }, full: { word: "w" },
}

const strictLabel = (name, args) => `${name}${args.length ? ` (${JSON.stringify(args[0])})` : ""}`
const strictFault = (name, args, rest) => {
  const message = `cannot encode ${S}${name}'s reply ${rest}`
  return { label: strictLabel(name, args), method: S + name, args, fault: `IC::Candid::EncodeError: ${message}`, shown: `fault: ${message}` }
}
const strictReply = (name, args, expect, shown) => ({ label: strictLabel(name, args), method: S + name, args, expect, shown })

// A step may run `as` "you" (this browser's saved key), "stranger" (a fresh key
// each run) or, by default, anonymous. `expect` may be a function of those
// principals' text.
// A step of a script: a reply, a fault or a refusal.
const step = (prefix, name, expect, shown, as) => ({ label: as ? `${name} as ${as}` : name, method: prefix + name, args: [], ...expect, shown, ...(as && { as }) })
const says = (text) => ({ expect: text })
const faults = (text) => ({ fault: text })
const refuses = (reason) => ({ refusal: reason })

const undoScript = (prefix, failing, failure, shown) => [
  step(prefix, "seed", says("seeded"), '"seeded"'),
  step(prefix, failing, failure, shown),
  step(prefix, "read", says("kept=before new=absent heap=1"), '"kept=before new=absent heap=1": the failed call left nothing behind'),
]
const Q = "query_discards_"
const W = "caller_"
const K = "controller_"
const T = "time_"
const FIVE_MINUTES = 5 * 60 * 1000
const whoIs = (who) => (ids) => ids[who]
const SAME = "same=true key=true anonymous=false roundtrip=true"
const OTHER = "same=false key=false anonymous=false roundtrip=true"
const ANON = "same=false key=false anonymous=true roundtrip=true"

export const CASES = {
  candid_primitives: [
    echo("nat", 0n, "(0)"), echo("nat", 1073741823n, "(2^30 - 1)"), echo("nat", 1073741824n, "(2^30)"),
    echo("nat", 1180591620717411303423n, "(2^70 - 1)"),
    echo("nat8", 0, "(0)"), echo("nat8", 255, "(255)"), echo("nat16", 0, "(0)"), echo("nat16", 65535, "(65535)"),
    echo("nat32", 0, "(0)"), echo("nat32", 4294967295, "(4294967295)"),
    echo("nat64", 0n, "(0)"), echo("nat64", 18446744073709551615n, "(2^64 - 1)"),
    echo("int8", -128, "(-128)"), echo("int8", 127, "(127)"), echo("int16", -32768, "(-32768)"), echo("int16", 32767, "(32767)"),
    echo("int32", -2147483648, "(-2^31)"), echo("int32", 2147483647, "(2^31 - 1)"),
    echo("int64", -9223372036854775808n, "(-2^63)"), echo("int64", 9223372036854775807n, "(2^63 - 1)"),
    echo("int", -1073741825n, "(-2^30 - 1)"), echo("int", -590295810358705651712n, "(-2^69)"), echo("int", 590295810358705651711n, "(2^69 - 1)"),
    echo("float32", 0.1, "(0.1, rounded to 32 bits)", Math.fround(0.1)),
    echo("float64", -0, "(-0.0, not 0.0)"), echo("float64", 1e308, "(1e308)"), echo("float64", 0.1, "(0.1)"),
    echo("bool", true, "(true)"), echo("bool", false, "(false)"),
    echo("text", "héllo \u{1F600} \u0000 end", "(multibyte text with a NUL)"),
    echo("blob", allBytes, "(every byte, 0 to 255)"),
    echo("principal", alice, "(aaaaa-aa)"),
    echo("null", null, "(null)"),
    echo("reserved", "anything at all", '("anything at all")', null),
    echo("opt", [], "(null)"), echo("opt", [5n], "(opt 5)"),
    echo("vec", [], "(vec {})"), echo("vec", vec40, "(40 integers, alternating sign)"),
    echo("record", nested, "(a nested record)"),
    echo("variant", { none: null }, "(variant none)"), echo("variant", { some: 4294967295 }, "(variant some)"), echo("variant", { word: "w" }, "(variant word)"),
    {
      label: "describe (every field)", method: `${P}describe`, args: [everything], shown: "which Ruby type each field arrives as",
      expect: "n=Integer n8=Integer n16=Integer n32=Integer n64=Integer i=Integer i8=Integer i16=Integer i32=Integer i64=Integer f32=Float f64=Float yes=TrueClass t=String/UTF-8 nul=nil res=nil p=IC::Principal b=String/ASCII-8BIT o=nil v=Array r=Hash/Symbol bare=Symbol full=Hash/Symbol",
    },
  ],

  candid_strict_reply: [
    strictFault("as_nat8", [], "(nat8): 300 is out of range"),
    strictFault("as_nat", ["float"], "(nat): 3.0 is a Float, not an Integer"),
    strictFault("as_nat", ["string"], '(nat): "3" is a String, not an Integer'),
    strictFault("as_record", ["extra"], "(record { a : nat8; b : opt text }): :c is not one of its fields"),
    strictFault("as_record", ["string_key"], '(record { a : nat8; b : opt text }): the key "a" is a String, not a Symbol'),
    strictFault("as_record", ["missing"], "at .a (nat8): missing"),
    strictReply("as_record", ["absent_opt"], { a: 1, b: [] }, "an absent opt key replies null"),
    strictFault("as_variant", ["unknown"], "(variant { ok : nat; none : null }): :nope is not one of its cases"),
    strictFault("as_variant", ["bare"], "(variant { ok : nat; none : null }): :ok carries a nat, so it is written { ok: value }"),
    strictFault("as_text", [], "(text): its bytes are not valid UTF-8"),
    strictFault("as_principal", [], '(principal): "2vxsx-fae" is a String, not an IC::Principal'),
    strictFault("as_empty", [], "(empty): no value fits empty"),
    strictReply("as_float", [], 3, "an Integer for a float replies 3.0"),
    strictFault("report", [], "at .items[2].amount (nat8): 300 is out of range"),
    strictFault("write_then_bad", [], "(nat8): 300 is out of range"),
    // After write_then_bad has finished, so the "undone" check is meaningful.
    { ...strictReply("written", [], false, "false: the handler's write was undone"), later: true },
  ],

  fault_undoes: undoScript("fault_undoes_", "write_then_raise", faults("ArgumentError: x"), "fault: ArgumentError: x"),
  refusal_undoes: undoScript("refusal_undoes_", "write_then_refuse", refuses("no"), "refused: no"),

  query_discards: [
    step(Q, "look", says("file=0 heap=0"), '"file=0 heap=0": an update sees nothing yet'),
    step(Q, "scribble", says("file=1 heap=1 hours=0"), '"file=1 heap=1 hours=0": the query sees its own write'),
    step(Q, "scribble", says("file=1 heap=1 hours=0"), '"file=1 heap=1 hours=0": the same again, the first was thrown away'),
    step(Q, "look", says("file=0 heap=0"), '"file=0 heap=0": the update still sees nothing'),
    step(Q, "refuse_in_query", refuses("a query keeps nothing"), "refused: a query keeps nothing"),
    step(Q, "look", says("file=0 heap=0"), '"file=0 heap=0": the refused query wrote nothing either'),
    step(Q, "who", says("2vxsx-fae"), '"2vxsx-fae": the caller, anonymous in this browser'),
  ],

  caller: [
    step(W, "whoami", says(whoIs("anonymous")), "the anonymous principal, 2vxsx-fae"),
    step(W, "whoami", says(whoIs("you")), "your principal, saved by this browser", "you"),
    step(W, "whoami", says(whoIs("stranger")), "the principal of a key made just now", "stranger"),
    step(W, "remember", says("remembered"), '"remembered": keeps your principal', "you"),
    step(W, "compare", says(SAME), `"${SAME}"`, "you"),
    step(W, "compare", says(OTHER), `"${OTHER}": a different caller`, "stranger"),
    step(W, "compare", says(ANON), `"${ANON}"`),
  ],

  controller: [
    step(K, "is_controller", says("controller=false"), '"controller=false": you did not deploy this canister', "you"),
    step(K, "is_controller", says("controller=false"), '"controller=false": nor is anonymous'),
    step(K, "guarded", refuses("only a controller may do this"), "refused: only a controller may do this", "you"),
  ],

  time: [
    step(T, "tick", says("integer=true forward=true time_now=true"), '"integer=true forward=true time_now=true"'),
    { ...step(T, "date", {}, "today's date, within five minutes of your browser's clock"),
      check: (reply, ctx) => Math.abs(Date.parse(reply) - ctx.now) < FIVE_MINUTES },
    step(T, "start", says("started"), '"started"'),
    step(T, "lapsed", says("lapsed=false"), '"lapsed=false": reads true only after 24 real hours'),
  ],
}
